import { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getBatchExtractions, deleteBatchExtraction } from '../../../services/batch-extractions';
import type { BatchExtractionSummary } from '../../../types/batch-extraction';
import { TablePagination } from '../../../components/TablePagination';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { AdminPage, AdminPageHeader } from '../AdminPage';
import './BatchExtractionsSection.css';

const UPLOAD_TYPE_LABELS: Record<string, string> = {
  single_team_roster: 'Team Roster',
  showcase: 'Showcase',
  // Backwards compat for existing DB records
  multi_page_roster: 'Team Roster',
  showcase_multi_team_page: 'Showcase',
  showcase_single_team_page: 'Showcase',
};

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  uploading: { label: 'Uploading', className: 'status--pending' },
  splitting: { label: 'Splitting PDF', className: 'status--pending' },
  extracting: { label: 'Extracting', className: 'status--processing' },
  review: { label: 'Ready for Review', className: 'status--success' },
  distributing: { label: 'Distributing', className: 'status--processing' },
  failed: { label: 'Failed', className: 'status--error' },
};

export const BatchExtractionsSection = () => {
  const navigate = useNavigate();

  const [batches, setBatches] = useState<BatchExtractionSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const fetchBatches = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getBatchExtractions(pageNumber, pageSize);
      setBatches(response.items);
      setTotalCount(response.totalCount);
    } catch (err) {
      console.error('Failed to fetch batch extractions:', err);
      setError('Failed to load batch extractions. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize]);

  useEffect(() => {
    fetchBatches();
  }, [fetchBatches]);

  const getBatchUrl = (batch: BatchExtractionSummary) => {
    // All statuses now go to review page since it handles progress display
    return `/admin/batch-extractions/${batch.id}/review`;
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    setDeleteLoading(true);
    try {
      await deleteBatchExtraction(deleteId);
      setDeleteId(null);
      fetchBatches();
    } catch (err) {
      console.error('Failed to delete batch:', err);
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <AdminPage width="full" className="batch-extractions">
      <AdminPageHeader
        title="Batch Extractions"
        actions={
          <button
            className="batch-extractions__add-btn"
            onClick={() => navigate('/admin/batch-extractions/new')}
          >
            New Batch
          </button>
        }
      />

      {error && (
        <div className="admin-error" role="alert">
          {error}
          <button onClick={fetchBatches} className="batch-extractions__retry-btn">
            Retry
          </button>
        </div>
      )}

      {loading ? (
        <div className="admin-state">Loading batch extractions...</div>
      ) : (
        <>
          <div className="batch-extractions__table-container">
            <table className="batch-extractions__table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Pages</th>
                  <th>Records</th>
                  <th>Status</th>
                  <th>Distributed</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="batch-extractions__empty">
                      No batch extractions found. Click "New Batch" to create one.
                    </td>
                  </tr>
                ) : (
                  batches.map((batch) => {
                    const statusInfo = STATUS_LABELS[batch.status] || {
                      label: batch.status,
                      className: '',
                    };
                    return (
                      <tr key={batch.id} className="batch-extractions__row">
                        <td className="batch-extractions__cell-name">
                          <Link to={getBatchUrl(batch)} className="batch-extractions__row-link">
                            {batch.name}
                          </Link>
                        </td>
                        <td>
                          <Link to={getBatchUrl(batch)} className="batch-extractions__row-link">
                            {UPLOAD_TYPE_LABELS[batch.uploadType] || batch.uploadType}
                          </Link>
                        </td>
                        <td>
                          <Link to={getBatchUrl(batch)} className="batch-extractions__row-link">
                            {batch.totalPages}
                          </Link>
                        </td>
                        <td>
                          <Link to={getBatchUrl(batch)} className="batch-extractions__row-link">
                            {batch.totalRecords}
                          </Link>
                        </td>
                        <td>
                          <Link to={getBatchUrl(batch)} className="batch-extractions__row-link">
                            <span className={`batch-extractions__status ${statusInfo.className}`}>
                              {statusInfo.label}
                            </span>
                          </Link>
                        </td>
                        <td>
                          <Link to={getBatchUrl(batch)} className="batch-extractions__row-link">
                            {batch.distributionCount > 0 ? (
                              <span className="batch-extractions__distribution-count">
                                {batch.distributionCount} org{batch.distributionCount !== 1 ? 's' : ''}
                              </span>
                            ) : (
                              <span className="batch-extractions__no-distribution">-</span>
                            )}
                          </Link>
                        </td>
                        <td>
                          <Link to={getBatchUrl(batch)} className="batch-extractions__row-link">
                            {formatDate(batch.createdAt)}
                          </Link>
                        </td>
                        <td>
                          <button
                            className="batch-extractions__delete-btn"
                            onClick={() => setDeleteId(batch.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <TablePagination
            currentPage={pageNumber}
            pageSize={pageSize}
            totalCount={totalCount}
            onPageChange={setPageNumber}
          />
        </>
      )}

      {deleteId && (
        <ConfirmModal
          isOpen={!!deleteId}
          title="Delete Batch"
          message="Are you sure you want to delete this batch extraction? This action cannot be undone."
          confirmText={deleteLoading ? 'Deleting...' : 'Delete'}
          cancelText="Cancel"
          onConfirm={handleDelete}
          onClose={() => setDeleteId(null)}
        />
      )}
    </AdminPage>
  );
};
